import { ReactNode, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useFormik } from 'formik';
import { Box } from '@/components/Box/Box';
import { ERROR_ASSIGN_EXECUTOR_AMPLITUDE_EVENTS_MAP, EVENTS_EXECUTOR_ACTION_TYPE } from '../../const/const';
import { assignExecutor, createExecutor, executorAssignmentStatus } from '../../api/executorApi';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { USER_ROLE_MAP } from '@/const/user';
import { loadUserInfo } from '@/bundle/shared/api';
import {
  ASSIGN_USER_INITIAL_VALUES,
  ASSIGN_USER_SCHEMA,
  AssignUserFormType,
} from '@/bundle/_OrgAdmin/shared/const/const';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { getIsAvailableToAssign } from '@/helpers/userHelpers';
import { AssignUserFormWrapper, FormMessage, FormTitle } from '@/bundle/_OrgAdmin/shared/ui/styles';
import { Notification } from '@/components/Notification/Notification';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { SearchUserEmailInput } from '@/bundle/shared/components/SearchUserEmailInput/SearchUserEmailInput';
import { useAssignUser } from '@/bundle/shared/hooks/useAssignUser';
import { HTTP_BAD_REQUEST_STATUS } from '@/api/const';

type AssignExecutorFormType = {
  onClose: () => void;
  onCreateExecutor: () => void;
  onAssignExecutor: () => void;
  header: ReactNode;
  subHeader: ReactNode;
};

type FormValues = {
  email: string;
  mobile_phone: string;
  first_name?: string;
  middle_name?: string;
  last_name?: string;
};

export const AssignExecutorForm = ({
  onClose,
  onCreateExecutor,
  onAssignExecutor,
  header,
  subHeader,
}: AssignExecutorFormType) => {
  const { id } = useParams<{ id: string }>();
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);

  const { data: assignmentStatusData } = useQuery({
    queryKey: ['load_assignment_status', assignedUserId],
    queryFn: () => executorAssignmentStatus(assignedUserId),
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_user', assignedUserId],
    queryFn: () => loadUserInfo(assignedUserId),
    enabled: !!assignedUserId && !!assignmentStatusData?.body?.is_available_to_assign,
  });

  const {
    mutate: createExecutorMutate,
    isPending: isPendingCreateExecutor,
    data: createExecutorData,
    reset: resetCreateExecutor,
  } = useMutation({
    mutationKey: ['create_executor'],
    mutationFn: (values: FormValues) => {
      return createExecutor(id, values);
    },
    onSuccess(response) {
      if (response?.error) return;

      amplitudeService.logEvent(AMPLITUDE_EVENTS.AssignNewExecutor);
      onCreateExecutor();
    },
  });

  const {
    mutate: assignExecutorMutate,
    isPending: isPendingAssignExecutor,
    data: assignExecutorData,
    reset: resetAssignExecutor,
  } = useMutation({
    mutationKey: ['assign_executor'],
    mutationFn: () => {
      const userData = loadUserData?.body;

      return assignExecutor(id, userData?.id);
    },
    onSuccess(response) {
      if (response?.error) return;

      const eventType = EVENTS_EXECUTOR_ACTION_TYPE[assignmentStatusData?.body?.code];

      amplitudeService.logEvent(eventType);
      onAssignExecutor();
    },
  });

  const isAvailableToAssign = getIsAvailableToAssign(assignmentStatusData?.body?.is_available_to_assign);
  const user = loadUserData?.body;

  const createExecutorError = createExecutorData?.error;
  const assignExecutorError = assignExecutorData?.error;
  const apiError = createExecutorError || assignExecutorError;
  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;
  const isLoading = isPendingCreateExecutor || isPendingAssignExecutor;
  const formError = getResponseError(apiError);

  const isDisabledForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const isDisabledValidation = !!user || (assignedUserId && !isAvailableToAssign);
  const isDisabledAssign = !isAvailableToAssign || !submittedEmailRequest || hasApiError;

  const formik = useFormik<AssignUserFormType>({
    initialValues: ASSIGN_USER_INITIAL_VALUES,
    validationSchema: isDisabledValidation ? null : ASSIGN_USER_SCHEMA,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (assignedUserId) {
        assignExecutorMutate();

        return;
      }

      createExecutorMutate(values);
    },
  });

  const assignmentStatus = assignmentStatusData?.body?.code;

  useEffect(() => {
    const assignmentStatusBody = assignmentStatusData?.body;

    if (assignmentStatusBody && !assignmentStatusBody?.is_available_to_assign) {
      amplitudeService.logEvent(ERROR_ASSIGN_EXECUTOR_AMPLITUDE_EVENTS_MAP[assignmentStatusBody?.code]);
    }
  }, [assignmentStatusData?.body]);

  const searchUserEmail = (userId: string) => {
    formik.setErrors({});
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.email && formik.errors?.email) {
      formik.setTouched({ email: true });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    resetCreateExecutor();
    resetAssignExecutor();
    formik.resetForm();
    formik.setFieldValue('email', formik.values.email);
  };

  useAssignUser(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    assignmentStatusData?.body?.is_available_to_assign,
    apiError
  );

  return (
    <AssignUserFormWrapper>
      <FormTitle>{header}</FormTitle>
      <FormMessage>{subHeader}</FormMessage>
      {formError && (
        <Notification variant='error' marginBottom='24px' marginTop='-10px'>
          {formError}
        </Notification>
      )}
      <FormikForm value={formik}>
        <SearchUserEmailInput
          role={USER_ROLE_MAP.EXECUTOR}
          email={formik.values.email}
          onSearch={searchUserEmail}
          onSubmit={submitUserEmail}
          assignmentStatus={assignmentStatus}
          error={formik.errors?.email}
          apiError={apiError}
        />

        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='first_name'
          label='First Name*'
          placeholder='Enter First Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />

        <Box alignItems='center' justifyContent='flex-end' columnGap='12px' marginTop='12px'>
          <Button variant='secondary' width={172} size='medium' onClick={onClose}>
            Cancel
          </Button>
          <Button width={172} size='medium' type='submit' disabled={isDisabledAssign} isLoading={isLoading}>
            Assign
          </Button>
        </Box>
      </FormikForm>
    </AssignUserFormWrapper>
  );
};
